import type { Median } from "../site/bishun-drawing.js";

/**
 * Where the stroke order pages draw each stroke's number and arrow.
 *
 * The arrow is drawn inside the stroke, along the first part of its median,
 * the line the brush follows in the outline data. It bends where the stroke
 * bends and points the way the brush moves, so a stroke written right to
 * left or bottom to top is drawn that way. Keeping it on the stroke's own ink
 * means two strokes that start close together do not put two arrows in the
 * same patch of white.
 *
 * The number sits just before the point where the brush goes down.
 *
 * Everything here is in the outline grid's units turned the right way up,
 * 1024 to a square with y growing downwards.
 */

/** A point on the grid, the right way up. */
export type Point = readonly [number, number];

/** The share of a stroke the arrow runs along, and its shortest and longest. */
const arrowShare = 0.6;
const shortestArrow = 80;
const longestArrow = 300;

/** How far back from the arrowhead its direction is measured. */
const headLook = 40;

/** How far before the stroke's start the number is centred. */
const labelGap = 64;

/** How long each barb of the arrowhead is, and its angle off the shaft. */
const barbLength = 36;
const barbAngle = (30 * Math.PI) / 180;

/** How close to the square's edge a number may be centred. */
const labelMargin = 64;

/** One stroke's arrow and the place for its number. */
export interface StrokeArrow {
  /** The arrow's shaft, from the stroke's start to the arrowhead. */
  readonly shaft: readonly Point[];
  /** The two ends of the arrowhead's barbs. */
  readonly barbs: readonly [Point, Point];
  /** The centre of the stroke's number. */
  readonly label: Point;
  /** The way the stroke leaves its start, which the number is set back along. */
  readonly leaving: Point;
}

function clamp(value: number): number {
  return Math.min(1024 - labelMargin, Math.max(labelMargin, value));
}

/** A point pulled inside the square, so a number at an edge is not cut off. */
function inside(point: Point): Point {
  return [clamp(point[0]), clamp(point[1])];
}

/** A median point turned the right way up. See `bishun-drawing.ts`. */
function upright(point: readonly [number, number]): Point {
  return [point[0], 900 - point[1]];
}

function distance(from: Point, to: Point): number {
  return Math.hypot(to[0] - from[0], to[1] - from[1]);
}

/** The unit vector from one point to another. */
function towards(from: Point, to: Point): Point {
  const length = distance(from, to);

  return [(to[0] - from[0]) / length, (to[1] - from[1]) / length];
}

/** The polyline from its start to so far along it. */
function upTo(points: readonly Point[], length: number): readonly Point[] {
  const kept: Point[] = [points[0]!];
  let left = length;

  for (let at = 1; at < points.length && left > 0; at += 1) {
    const from = points[at - 1]!;
    const to = points[at]!;
    const step = distance(from, to);
    if (step >= left) {
      const share = left / step;
      kept.push([
        from[0] + (to[0] - from[0]) * share,
        from[1] + (to[1] - from[1]) * share,
      ]);

      return kept;
    }
    kept.push(to);
    left -= step;
  }

  return kept;
}

/** The length of a polyline. */
function lengthOf(points: readonly Point[]): number {
  let total = 0;
  for (let at = 1; at < points.length; at += 1) {
    total += distance(points[at - 1]!, points[at]!);
  }

  return total;
}

/**
 * The arrow and number for one stroke.
 * @param median - The stroke's median, as the outline data gives it.
 * @returns Nothing for a median with no length, which has no direction to
 * show.
 */
export function strokeArrow(median: Median): StrokeArrow | undefined {
  const points = median.map((point) => upright(point));
  const total = lengthOf(points);
  if (total === 0) {
    return undefined;
  }

  const shaft = upTo(
    points,
    Math.min(
      total,
      Math.min(longestArrow, Math.max(shortestArrow, total * arrowShare)),
    ),
  );
  const head = shaft.at(-1)!;
  const shaftLength = lengthOf(shaft);
  const back = upTo(shaft, Math.max(0, shaftLength - headLook)).at(-1)!;
  const pointing = towards(back, head);
  const barb = (turn: number): Point => {
    const angle = Math.atan2(-pointing[1], -pointing[0]) + turn;

    return [
      head[0] + Math.cos(angle) * barbLength,
      head[1] + Math.sin(angle) * barbLength,
    ];
  };

  const start = points[0]!;
  const setOff = towards(
    start,
    upTo(points, Math.min(total, headLook)).at(-1)!,
  );

  return {
    shaft,
    barbs: [barb(barbAngle), barb(-barbAngle)],
    label: inside([
      start[0] - setOff[0] * labelGap,
      start[1] - setOff[1] * labelGap,
    ]),
    leaving: setOff,
  };
}

/**
 * How large a character's numbers are set, on the outline grid.
 *
 * A character of a few strokes has room for large numbers, and 爨 has thirty
 * strokes and a two-digit number on most of them. The size falls from 96
 * units at six strokes to 52 at twenty-five and over, which is 4.4 mm in the
 * larger square and still clear on an office printer.
 */
export function labelSize(strokeCount: number): number {
  return Math.round(Math.min(96, Math.max(52, 96 - (strokeCount - 6) * 2.3)));
}

/** The space kept around a number, as a share of its size. */
const labelPadding = 0.18;

/** The half-width and half-height of a number's box, at a size. */
function labelBox(label: number, size: number): Point {
  const digits = String(label).length;

  return [size * (0.3 * digits + labelPadding), size * (0.38 + labelPadding)];
}

/** How far a number may be tried from its stroke's start, in its own sizes. */
const labelRadii: readonly number[] = [0.7, 1.05, 1.45, 1.9];

/** The turns off "straight back along the stroke" a number is tried at. */
const labelTurns: readonly number[] = [
  0, 0.25, -0.25, 0.5, -0.5, 0.75, -0.75, 1,
].map((share) => share * Math.PI);

/** Points along a polyline no further apart than `step`. */
function sampled(points: readonly Point[], step: number): readonly Point[] {
  const samples: Point[] = [points[0]!];
  for (let at = 1; at < points.length; at += 1) {
    const from = points[at - 1]!;
    const to = points[at]!;
    const steps = Math.max(1, Math.ceil(distance(from, to) / step));
    for (let part = 1; part <= steps; part += 1) {
      samples.push([
        from[0] + ((to[0] - from[0]) * part) / steps,
        from[1] + ((to[1] - from[1]) * part) / steps,
      ]);
    }
  }

  return samples;
}

/**
 * How far a point is outside a box, as a share of the box: under 1 inside.
 */
function outside(point: Point, centre: Point, box: Point): number {
  return Math.max(
    Math.abs(point[0] - centre[0]) / box[0],
    Math.abs(point[1] - centre[1]) / box[1],
  );
}

/**
 * Put every number where it prints clear of the other numbers and the arrows.
 *
 * Two strokes often start at the same point, as the first two of 口 do, and a
 * dense character has arrows running close past the start of other strokes.
 * Each number is measured as the box its digits fill, so 23 keeps twice the
 * room of 3. It is tried at a ring of places around its stroke's start,
 * nearest and straight back along the stroke first, and takes the first that
 * clears every number already placed and every arrow. Where none does, it
 * takes the place with the most room.
 * @param arrows - Every stroke's arrow, in writing order.
 * @param size - The numbers' size, from `labelSize`.
 * @returns The same arrows, each with its number placed.
 */
export function placeLabels(
  arrows: readonly StrokeArrow[],
  size: number,
): readonly StrokeArrow[] {
  const shafts = arrows.flatMap((arrow) => sampled(arrow.shaft, size / 6));
  const placed: { readonly centre: Point; readonly box: Point }[] = [];

  return arrows.map((arrow, at) => {
    const box = labelBox(at + 1, size);
    const start = arrow.shaft[0]!;
    const back = Math.atan2(-arrow.leaving[1], -arrow.leaving[0]);
    const room = (centre: Point): number =>
      Math.min(
        ...placed.map((other) =>
          outside(centre, other.centre, [
            box[0] + other.box[0],
            box[1] + other.box[1],
          ]),
        ),
        ...shafts.map((sample) => outside(sample, centre, box)),
      );
    const tried = labelRadii.flatMap((radius) =>
      labelTurns.map((turn): Point =>
        inside([
          start[0] + Math.cos(back + turn) * radius * size,
          start[1] + Math.sin(back + turn) * radius * size,
        ]),
      ),
    );
    const label =
      tried.find((centre) => room(centre) >= 1) ??
      tried.reduce((best, centre) =>
        room(centre) > room(best) ? centre : best,
      );
    placed.push({ centre: label, box });

    return { ...arrow, label };
  });
}
